import { cn } from '@repo/tailwind-config/tailwind.functions'
import { tailwindColors } from '@repo/tailwind-config/tailwind.vars'
import Box from '@repo/ui/box'
import Checkbox from '@repo/ui/checkbox'
import { InformationIcon } from '@repo/ui/icons'
import InnerBox from '@repo/ui/inner-box'
import Typography from '@repo/ui/typography'
import { useEffect, useState } from 'react'
import { useFormContext } from 'react-hook-form'

import {
  LanguageLevelEnum,
  languageLevelEnumMapping
} from '../../../../lib/types/enums/languages/language-level.enum'
import { EnrollmentChildInput } from '../../../../lib/types/models/enrollment/enrollment-child-input'
import { FormControl, FormField, FormItem } from '../../../elements/forms/form'
import ControlledInput from '../../../elements/inputs/controlled-input'
import ControlledMultiSelectInput from '../../../elements/inputs/controlled-multi-select-input'
import ControlledPhoneInput from '../../../elements/inputs/controlled-phone-input'
import ControlledRadioInput from '../../../elements/inputs/controlled-radio-input'
import { spokenLanguages } from './utils'

type Props = {
  version?: 'short' | 'long'
}

const ClientEnrollmentChildInformationSection: React.FC<Props> = ({ version }) => {
  const form = useFormContext<EnrollmentChildInput>()

  const selectedLanguageNames = form.watch('spokenLanguages') || []

  // Separate predefined languages (with flags) from custom ones
  const predefinedLanguageNames = spokenLanguages.map((lang) => lang.label)
  const customLanguageNames = selectedLanguageNames.filter(
    (name) => !predefinedLanguageNames.includes(name)
  )

  // Initialize state based on whether there are custom languages
  const [showOtherLanguageInput, setShowOtherLanguageInput] = useState(() => {
    return customLanguageNames.length > 0
  })

  // Update the state when the form values change (e.g., when form is reset or loaded with new data)
  useEffect(() => {
    const hasCustomLanguages = customLanguageNames.length > 0
    setShowOtherLanguageInput(hasCustomLanguages)
  }, [customLanguageNames.length])

  const handleCustomLanguagesChange = (languageNames: string[]) => {
    const currentSelected = form.getValues('spokenLanguages') || []

    // Keep predefined languages that were already selected
    const selectedPredefinedLanguages = currentSelected.filter((name) =>
      predefinedLanguageNames.includes(name)
    )

    //if no custom languages, reset the field
    if (languageNames.length === 0) {
      setShowOtherLanguageInput(false)
      form.setValue('spokenLanguages', selectedPredefinedLanguages)
      return
    }

    // Combine predefined + new custom languages, maintaining order
    const updatedLanguages = [...selectedPredefinedLanguages, ...languageNames]

    form.setValue('spokenLanguages', updatedLanguages)
  }

  const didntAgreeToHouseRules = form.formState.errors.agreesToHouseRulesAndNoSmoking

  return (
    <InnerBox className="rounded-lg p-4 flex flex-col gap-4 mobile:gap-3">
      <Typography variant="text" color={tailwindColors.grey.DEFAULT}>
        Informations sur le participant
      </Typography>
      <div className="flex gap-3 items-center mobile:flex-col">
        <FormField
          control={form.control}
          name={'childEmail'}
          render={({ field }) => (
            <FormItem className={'w-full'}>
              <FormControl>
                <ControlledInput {...field} label="Email" type="email" />
              </FormControl>
            </FormItem>
          )}
        />
        <FormField
          control={form.control}
          name={'childPhoneNumber'}
          render={({ field }) => (
            <FormItem className={'w-full'}>
              <FormControl>
                <ControlledPhoneInput {...field} label="Numéro de téléphone" defaultCountry="FR" />
              </FormControl>
            </FormItem>
          )}
        />
      </div>
      <div className="flex flex-wrap items-center gap-5 mobile:flex-col mobile:gap-2 mobile:items-start">
        <Typography variant="label-semibold" color={tailwindColors.grey.DEFAULT}>
          Niveau de la langue du pays choisi *
        </Typography>
        <div className="flex items-center gap-3">
          {Object.values(LanguageLevelEnum)
            .filter((level) => level !== LanguageLevelEnum.NOT_PRACTICED)
            .map((level) => (
              <FormField
                key={level}
                control={form.control}
                name={'chosenCountryLanguageLevel'}
                render={({ field }) => (
                  <FormItem>
                    <FormControl>
                      <ControlledRadioInput
                        {...field}
                        label={languageLevelEnumMapping[level].label}
                        checked={field.value === level}
                        onCheck={() =>
                          form.setValue('chosenCountryLanguageLevel', level, {
                            shouldValidate: true
                          })
                        }
                      />
                    </FormControl>
                  </FormItem>
                )}
              />
            ))}
        </div>
      </div>
      <div className="flex items-starts gap-2 flex-col">
        <Typography variant="label-semibold" color={tailwindColors.grey.DEFAULT}>
          Autres langues étrangères étudiées ou parlées
        </Typography>
        <div className="grid grid-cols-4 mobile:grid-cols-2 gap-2.5">
          {spokenLanguages.map((language) => (
            <Checkbox
              key={language.label}
              checked={selectedLanguageNames.includes(language.label)}
              component={
                <div className="flex items-center gap-1 cursor-pointer">
                  {language.flag}
                  <Typography variant="label" color={tailwindColors.grey.DEFAULT}>
                    {language.label}
                  </Typography>
                </div>
              }
              onChange={(checked) => {
                const currentNames = form.getValues('spokenLanguages') || []
                if (checked) {
                  form.setValue('spokenLanguages', [...currentNames, language.label])
                } else {
                  form.setValue(
                    'spokenLanguages',
                    currentNames.filter((name) => name !== language.label)
                  )
                }
              }}
            />
          ))}

          <Checkbox
            checked={showOtherLanguageInput}
            component={
              <div className="flex items-center gap-1 cursor-pointer">
                <Typography variant="label" color={tailwindColors.grey.DEFAULT}>
                  Autres langue
                </Typography>
              </div>
            }
            onChange={(checked) => {
              setShowOtherLanguageInput(checked)

              // If unchecking, remove all custom languages from the form
              if (!checked) {
                const currentSelected = form.getValues('spokenLanguages') || []
                const onlyPredefinedLanguages = currentSelected.filter((name) =>
                  predefinedLanguageNames.includes(name)
                )
                form.setValue('spokenLanguages', onlyPredefinedLanguages)
              }
            }}
          />
        </div>

        {showOtherLanguageInput && (
          <FormField
            control={form.control}
            name="spokenLanguages"
            render={() => (
              <FormItem className="flex-1">
                <FormControl>
                  <ControlledMultiSelectInput<string>
                    label="Autres langues"
                    type="text"
                    variant="primary"
                    onChange={handleCustomLanguagesChange}
                    selectedOptions={customLanguageNames.map((name) => ({
                      label: name,
                      value: name
                    }))}
                  />
                </FormControl>
              </FormItem>
            )}
          />
        )}
      </div>
      {version === 'long' && (
        <div className="flex flex-col gap-1.5">
          <div className="flex items-center gap-5 mobile:flex-col mobile:gap-2 mobile:items-start">
            <Typography variant="label-semibold" color={tailwindColors.grey.DEFAULT}>
              Est-ce que le participant fume ? *
            </Typography>

            <div className="flex items-center gap-3">
              <FormField
                control={form.control}
                name={'doesChildSmoke'}
                render={({ field }) => (
                  <FormItem>
                    <FormControl>
                      <ControlledRadioInput
                        {...field}
                        label={'Oui'}
                        checked={field.value === true}
                        onCheck={() =>
                          form.setValue('doesChildSmoke', true, { shouldValidate: true })
                        }
                      />
                    </FormControl>
                  </FormItem>
                )}
              />
              <FormField
                control={form.control}
                name={'doesChildSmoke'}
                render={({ field }) => (
                  <FormItem>
                    <FormControl>
                      <ControlledRadioInput
                        {...field}
                        label={'Non'}
                        checked={field.value === false}
                        onCheck={() => {
                          form.setValue('doesChildSmoke', false, { shouldValidate: true })
                          form.setValue('agreesToHouseRulesAndNoSmoking', false, {
                            shouldValidate: true
                          })
                        }}
                      />
                    </FormControl>
                  </FormItem>
                )}
              />
            </div>
          </div>
          {form.watch('doesChildSmoke') && (
            <InnerBox
              className={cn('rounded-sm flex flex-row items-center justify-between p-2 gap-1', {
                'border border-secondaryRed': didntAgreeToHouseRules
              })}
            >
              <Typography
                variant="label-medium"
                color={tailwindColors.grey.DEFAULT}
                className="pr-3"
              >
                Si oui, il s’engage à respecter les règles du séjour et à ne pas fumer pendant
                toute la durée du séjour :
              </Typography>
              <div
                className={cn(
                  'rounded-xs flex items-center p-1 gap-1 bg-primary-light border border-primary-light-hover',
                  {
                    'bg-red-light border-red-light': didntAgreeToHouseRules
                  }
                )}
              >
                <Checkbox
                  checked={form.watch('agreesToHouseRulesAndNoSmoking') || false}
                  onChange={(checked) =>
                    form.setValue('agreesToHouseRulesAndNoSmoking', checked ? true : null, {
                      shouldValidate: true
                    })
                  }
                />
                <Typography
                  variant="label"
                  color={
                    didntAgreeToHouseRules
                      ? tailwindColors.secondaryRed.DEFAULT
                      : tailwindColors.grey.DEFAULT
                  }
                >
                  Oui
                </Typography>
              </div>
            </InnerBox>
          )}
          <Box className="bg-secondaryRed-light rounded-sm p-2 flex items-center gap-1">
            <div className="flex-shrink-0">
              <InformationIcon
                color={tailwindColors.secondaryRed.DEFAULT}
                height="20"
                width="20"
              />
            </div>
            <Typography variant="label-medium" color={tailwindColors.secondaryRed.DEFAULT}>
              <strong>NB</strong> : L’achat, la possession et la consommation de cigarette(s) et
              cigarette électronique sont illégaux pour les mineurs.
            </Typography>
          </Box>
        </div>
      )}
    </InnerBox>
  )
}

export default ClientEnrollmentChildInformationSection
